import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User as UserIcon } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { paths } from 'shared/routing/paths';

/** Matches aligned's `_profileImageUrl` cache check — only a Firebase-Storage-hosted profile photo is trusted, not any arbitrary URL. */
function isCachedProfileImageUrl(url: string | undefined): url is string {
  return !!url && url.includes('firebasestorage.googleapis.com') && url.includes('profile_images');
}

function AvatarCircle({ url, sizeClass, iconClass, bgClass }: { url: string | null; sizeClass: string; iconClass: string; bgClass: string }) {
  if (url) {
    return <img src={url} alt="" className={`${sizeClass} rounded-full object-cover`} />;
  }
  return (
    <div className={`flex ${sizeClass} items-center justify-center rounded-full ${bgClass}`}>
      <UserIcon className={iconClass} />
    </div>
  );
}

interface MenuLink {
  label: string;
  to: string;
}

/**
 * Ported from aligned's shared `CommonProfileMenuButton`/`ProfileMenuOverlay`
 * — a profile-avatar button that opens a dropdown with account settings,
 * cross-area navigation (for accounts with more than one role), and logout.
 * Avatar priority matches aligned's `_profileImageUrl`: a cached Firebase
 * Storage profile photo, then the Firebase Auth `photoURL` (e.g. Google
 * sign-in), then a generic silhouette (a lucide icon here, not aligned's
 * bundled PNG asset — this port is already icon-based throughout).
 *
 * Menu item gating matches aligned exactly: Settings always goes to the
 * expert personal-settings screen regardless of which area you're currently
 * in (aligned has no area-specific settings destination for this item);
 * "Admin Tools" is available to full and contractor admins; "User Account" /
 * "Manager Account" remain full-admin-only (there's no separate `isManager`
 * field to gate on, matching `auth/roles.ts`'s own note); "Operations Tools"
 * is operations-manager-or-admin.
 */
export function ProfileMenu() {
  const { currentUser, userAccount, isAdmin, isOperationsManager, isContractorAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const storedProfilePictureUrl = userAccount?.profilePictureUrl;
  const avatarUrl = isCachedProfileImageUrl(storedProfilePictureUrl) ? storedProfilePictureUrl : currentUser?.photoURL ?? null;

  const firstName = userAccount?.firstNamePreferred || userAccount?.firstName;
  const displayName = firstName
    ? `${firstName}${userAccount?.lastName ? ` ${userAccount.lastName}` : ''}`
    : currentUser?.displayName || currentUser?.email || 'Account';

  const links: MenuLink[] = [{ label: 'Settings', to: paths.expert.settingsPersonal }];
  if (isAdmin || isContractorAdmin) links.push({ label: 'Admin Tools', to: paths.admin.root });
  if (isOperationsManager || isAdmin) links.push({ label: 'Operations Tools', to: paths.operations.root });
  if (isAdmin) links.push({ label: 'User Account', to: paths.expert.root });
  if (isAdmin) links.push({ label: 'Manager Account', to: paths.manager.root });

  const goTo = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Profile menu"
        className="rounded-full focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <AvatarCircle url={avatarUrl} sizeClass="h-8 w-8" iconClass="h-4 w-4 text-white" bgClass="bg-white/20" />
      </button>

      {open ? (
        <div className="absolute right-0 z-20 mt-2 w-64 rounded-md border border-slate-200 bg-white py-2 text-slate-900 shadow-lg">
          <div className="flex items-center gap-3 border-b border-slate-100 px-4 pb-3">
            <AvatarCircle url={avatarUrl} sizeClass="h-10 w-10" iconClass="h-5 w-5 text-slate-500" bgClass="bg-slate-200" />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">{displayName}</p>
              <p className="truncate text-xs text-slate-500">{currentUser?.email}</p>
            </div>
          </div>

          <div className="py-1">
            {links.map((link) => (
              <button
                key={link.to}
                type="button"
                onClick={() => goTo(link.to)}
                className="block w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                {link.label}
              </button>
            ))}
          </div>

          <div className="border-t border-slate-100 pt-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
              className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              Log Out
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
